import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { GraphQLVotingService } from '../../services/graphqlVoting';
import { useAuth } from '../../contexts/AuthContext';
import { loadAllUsers } from '../../services/userLoader';
import PageHeader from '../common/PageHeader';
import './CreateVotingPoll.css';

interface SimpleUser {
  id: string;
  email: string;
  name: string;
  department: string;
  role: string;
}

const CreateVotingPoll: React.FC<{ onComplete?: () => void; embedded?: boolean }> = ({ onComplete, embedded }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [deadline, setDeadline] = useState('');
  const [allUsers, setAllUsers] = useState<SimpleUser[]>([]);
  const [selectedNominees, setSelectedNominees] = useState<string[]>([]);
  const [selectedVoters, setSelectedVoters] = useState<string[]>([]);
  const [nomineeSearch, setNomineeSearch] = useState('');
  const [voterSearch, setVoterSearch] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const users = await loadAllUsers();
        setAllUsers(users.filter(u => u.email !== user?.email));
      } catch (e) {
        console.error('Failed to load users:', e);
      }
    };
    fetchUsers();
  }, [user]);

  const toggleNominee = (email: string) => {
    setSelectedNominees(prev => prev.includes(email) ? prev.filter(e => e !== email) : [...prev, email]);
  };

  const toggleVoter = (email: string) => {
    setSelectedVoters(prev => prev.includes(email) ? prev.filter(e => e !== email) : [...prev, email]);
  };

  const getUserName = (email: string) => allUsers.find(u => u.email === email)?.name || email;

  const filteredNominees = allUsers.filter(u =>
    u.name.toLowerCase().includes(nomineeSearch.toLowerCase()) ||
    u.email.toLowerCase().includes(nomineeSearch.toLowerCase())
  );

  const filteredVoters = allUsers.filter(u =>
    !selectedNominees.includes(u.email) &&
    (u.name.toLowerCase().includes(voterSearch.toLowerCase()) ||
     u.email.toLowerCase().includes(voterSearch.toLowerCase()))
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setError('');
    setSubmitting(true);

    const result = await GraphQLVotingService.createVotingPoll(
      {
        title: title.trim(),
        description: description.trim() || undefined,
        nominees: selectedNominees,
        nomineeNames: selectedNominees.map(getUserName),
        voters: selectedVoters,
        voterNames: selectedVoters.map(getUserName),
        deadline: deadline ? new Date(deadline).toISOString() : undefined,
      },
      user.email,
      user.name
    );

    setSubmitting(false);
    if (result.success) {
      if (onComplete) onComplete(); else navigate('/voting');
    } else {
      setError(result.errors.join(', '));
    }
  };

  return (
    <div className={embedded ? 'cvp-embedded' : 'cvp-page'}>
      {!embedded && (
        <PageHeader
          icon={<span style={{ fontSize: '1.5rem' }}>🗳️</span>}
          title="Create Voting Poll"
          subtitle="Select nominees and voters for the poll"
          gradient="linear-gradient(135deg, #7c3aed, #6d28d9)"
          accentColor="#7c3aed"
        />
      )}

      <form className="cvp-form" onSubmit={handleSubmit}>
        {error && <div className="cvp-error">{error}</div>}

        <div className="cvp-field">
          <label className="cvp-label">Poll Title *</label>
          <input className="cvp-input" value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Best Employee Q1 2026" required />
        </div>

        <div className="cvp-field">
          <label className="cvp-label">Description</label>
          <textarea className="cvp-textarea" value={description} onChange={e => setDescription(e.target.value)} placeholder="Optional description..." rows={3} />
        </div>

        <div className="cvp-field">
          <label className="cvp-label">Deadline</label>
          <input className="cvp-input" type="datetime-local" value={deadline} onChange={e => setDeadline(e.target.value)} />
        </div>

        <div className="cvp-field">
          <label className="cvp-label">Nominees * (select at least 2)</label>
          <input className="cvp-input" value={nomineeSearch} onChange={e => setNomineeSearch(e.target.value)} placeholder="Search employees..." />
          {selectedNominees.length > 0 && (
            <div className="cvp-chips">
              {selectedNominees.map(email => (
                <span key={email} className="cvp-chip nominee">
                  {getUserName(email)} <button type="button" onClick={() => toggleNominee(email)}>×</button>
                </span>
              ))}
            </div>
          )}
          <div className="cvp-user-list">
            {filteredNominees.slice(0, 10).map(u => (
              <label key={u.email} className={`cvp-user-item ${selectedNominees.includes(u.email) ? 'selected' : ''}`}>
                <input type="checkbox" checked={selectedNominees.includes(u.email)} onChange={() => toggleNominee(u.email)} />
                <div className="cvp-user-info">
                  <span className="cvp-user-name">{u.name}</span>
                  <span className="cvp-user-email">{u.email}</span>
                </div>
                <span className="cvp-user-dept">{u.department}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="cvp-field">
          <label className="cvp-label">Voters * (who should vote — they'll be notified)</label>
          <input className="cvp-input" value={voterSearch} onChange={e => setVoterSearch(e.target.value)} placeholder="Search managers/employees..." />
          {selectedVoters.length > 0 && (
            <div className="cvp-chips">
              {selectedVoters.map(email => (
                <span key={email} className="cvp-chip voter">
                  {getUserName(email)} <button type="button" onClick={() => toggleVoter(email)}>×</button>
                </span>
              ))}
            </div>
          )}
          <div className="cvp-user-list">
            {filteredVoters.slice(0, 10).map(u => (
              <label key={u.email} className={`cvp-user-item ${selectedVoters.includes(u.email) ? 'selected' : ''}`}>
                <input type="checkbox" checked={selectedVoters.includes(u.email)} onChange={() => toggleVoter(u.email)} />
                <div className="cvp-user-info">
                  <span className="cvp-user-name">{u.name}</span>
                  <span className="cvp-user-email">{u.email}</span>
                </div>
                <span className="cvp-user-dept">{u.department}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="cvp-actions">
          <button type="button" className="cvp-btn-cancel" onClick={() => { if (onComplete) onComplete(); else navigate('/voting'); }}>Cancel</button>
          <button type="submit" className="cvp-btn-submit" disabled={submitting || selectedNominees.length < 2 || selectedVoters.length < 1}>
            {submitting ? 'Creating...' : 'Create Poll & Notify Voters'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateVotingPoll;
